import { z } from 'zod';
import {
  NO_FACET_MATCH,
  boardFilterKeywordSchema,
  dueBucketSchema,
} from './board-filter';
import { ulidSchema } from './id';
import { publicProfileSchema } from './user';

/**
 * Saved Workspace views — block B55.
 *
 * These are durable cross-board Table/Calendar configurations. The filter
 * shape is a JSON-body sibling of the Workspace Table/Calendar querystrings:
 * it reuses the primitive vocabularies, but keeps only the facets those
 * Workspace views accept.
 */

export const WORKSPACE_VIEW_KINDS = ['table', 'calendar'] as const;
export const WORKSPACE_VIEW_VISIBILITIES = ['private', 'workspace_visible'] as const;

/** `workspace_views.title varchar(255)`. */
export const WORKSPACE_VIEW_TITLE_MAX_LENGTH = 255;

/** B50/B51 selected-board cap, persisted per saved view in B55. */
export const WORKSPACE_VIEW_BOARD_LIMIT = 20;

export const workspaceViewKindSchema = z.enum(WORKSPACE_VIEW_KINDS);
export type WorkspaceViewKind = z.infer<typeof workspaceViewKindSchema>;

export const workspaceViewVisibilitySchema = z.enum(WORKSPACE_VIEW_VISIBILITIES);
export type WorkspaceViewVisibility = z.infer<typeof workspaceViewVisibilitySchema>;

export const workspaceViewTitleSchema = z
  .string()
  .trim()
  .min(1)
  .max(WORKSPACE_VIEW_TITLE_MAX_LENGTH);
export type WorkspaceViewTitle = z.infer<typeof workspaceViewTitleSchema>;

export const workspaceViewFacetOptionSchema = z.union([
  z.literal(NO_FACET_MATCH),
  ulidSchema,
]);
export type WorkspaceViewFacetOption = z.infer<typeof workspaceViewFacetOptionSchema>;

export const workspaceViewSelectedBoardIdsSchema = z
  .array(ulidSchema)
  .min(1)
  .max(WORKSPACE_VIEW_BOARD_LIMIT);
export type WorkspaceViewSelectedBoardIds = z.infer<
  typeof workspaceViewSelectedBoardIdsSchema
>;

export const workspaceViewStoredBoardIdsSchema = z
  .array(ulidSchema)
  .max(WORKSPACE_VIEW_BOARD_LIMIT);
export type WorkspaceViewStoredBoardIds = z.infer<
  typeof workspaceViewStoredBoardIdsSchema
>;

export const workspaceViewFiltersSchema = z.strictObject({
  members: z.array(workspaceViewFacetOptionSchema),
  labels: z.array(workspaceViewFacetOptionSchema),
  lists: z.array(ulidSchema),
  due: z.array(dueBucketSchema),
  keyword: boardFilterKeywordSchema.optional(),
});
export type WorkspaceViewFilters = z.infer<typeof workspaceViewFiltersSchema>;

export const workspaceViewWorkspaceRefSchema = z.strictObject({
  id: ulidSchema,
});
export type WorkspaceViewWorkspaceRef = z.infer<typeof workspaceViewWorkspaceRefSchema>;

export const workspaceViewSchema = z.strictObject({
  id: ulidSchema,
  workspace: workspaceViewWorkspaceRefSchema,
  creator: publicProfileSchema,
  title: z.string().min(1).max(WORKSPACE_VIEW_TITLE_MAX_LENGTH),
  kind: workspaceViewKindSchema,
  visibility: workspaceViewVisibilitySchema,
  selectedBoardIds: workspaceViewStoredBoardIdsSchema,
  filters: workspaceViewFiltersSchema,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});
export type WorkspaceView = z.infer<typeof workspaceViewSchema>;

export const workspaceViewCreateBodySchema = z.strictObject({
  title: workspaceViewTitleSchema,
  kind: workspaceViewKindSchema,
  visibility: workspaceViewVisibilitySchema.optional().default('private'),
  selectedBoardIds: workspaceViewSelectedBoardIdsSchema,
  filters: workspaceViewFiltersSchema.optional(),
});
export type WorkspaceViewCreateBody = z.infer<typeof workspaceViewCreateBodySchema>;

export const workspaceViewTitleBodySchema = z.strictObject({
  title: workspaceViewTitleSchema,
});
export type WorkspaceViewTitleBody = z.infer<typeof workspaceViewTitleBodySchema>;

export const workspaceViewVisibilityBodySchema = z.strictObject({
  visibility: workspaceViewVisibilitySchema,
});
export type WorkspaceViewVisibilityBody = z.infer<
  typeof workspaceViewVisibilityBodySchema
>;

export const workspaceViewBoardsBodySchema = z.strictObject({
  selectedBoardIds: workspaceViewSelectedBoardIdsSchema,
});
export type WorkspaceViewBoardsBody = z.infer<typeof workspaceViewBoardsBodySchema>;

export const workspaceViewFiltersBodySchema = workspaceViewFiltersSchema;
export type WorkspaceViewFiltersBody = z.infer<typeof workspaceViewFiltersBodySchema>;
